import { faPlus, faUndo } from '@fortawesome/free-solid-svg-icons';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import ButtonIcon from '../common/ButtonIcon';
import Flex from '../common/Flex';
import PageHeader from '../common/PageHeader';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid'
import bootstrapPlugin from '@fullcalendar/bootstrap';
import interactionPlugin from '@fullcalendar/interaction';
import AddScheduleModal from './components/AddReservationModal';
import CalendarEventModal from './components/CalendarEventModal';
import moment from 'moment';
import { getReservations } from '../../helpers/api/areas';
import handleErrorMessage from '../../helpers/errors/handleErrorMessage';
import { If, Or, PreloadState } from '../UI/preloading/PreloadState';
import { useSelector } from 'react-redux';
import SupportLink from '../root/support/components/SupportLink';

const Reservations = () => {

    const startOfMonth = moment().startOf('month').format('YYYY-MM-DD hh:mm');
    const endOfMonth = moment().endOf('month').add(1, 'day').format('YYYY-MM-DD hh:mm');
    const { role } = useSelector(state => state.auth.user);
    const { id } = useParams();
    const { state } = useLocation();
    const calendarRef = useRef();
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isOpenScheduleModal, setIsOpenScheduleModal] = useState(false);
    const [modalEventContent, setModalEventContent] = useState(false);
    const [addScheduleStartDate, setAddScheduleStartDate] = useState();
    const [events, setEvents] = useState([])
    const [data, setData] = useState({ data: [], isLoading: true });
    const [params, setParams] = useState({
        commonArea: id || null,
        me: false,
        start_at: startOfMonth,
        end_at: endOfMonth,
        status: null,
        count: 0
    });

    useEffect(() => {
        let isMounted = true;
        const ac = new AbortController();
        isMounted && setData(prev => ({ ...prev, isLoading: true }));
        !!id && getReservations(params, { signal: ac.signal })
            .then(({ data }) => isMounted && setData(prev => ({ ...prev, data })))
            .catch(error => handleErrorMessage(error))
            .finally(() => isMounted && setData(prev => ({ ...prev, isLoading: false })))
        return () => {
            ac.abort();
            isMounted = false;
        };

    }, [id, params])

    const mutate = () => {
        setParams(prev => ({ ...prev, count: Math.floor(Math.random() * 100) }))
    }

    useEffect(() => {
        let isMounted = true;
        if (data.data.length > 0) {
            isMounted && setEvents(data.data.map(({ start_at, end_at, description, reserved_common_area }) => ({
                title: description,
                start: start_at,
                end: end_at,
                extendedProps: { id: reserved_common_area },
                className: 'bg-primary border-0 p-2 font-weight-bold'
            })))
        } else {
            isMounted && setEvents([]);
        }
        return () => {
            isMounted = false;
        }
    }, [data])

    const eventList = events.reduce(
        (acc, event) => (event.schedules ? acc.concat(event.schedules.concat(event)) : acc.concat(event)),
        []
    );

    const [initialEvents, setInitialEvents] = useState(eventList);

    const history = useHistory();

    const buttonText = {
        today: 'Today',
        month: 'Month view',
        week: 'week',
        day: 'day',
        listWeek: 'list view',
        listYear: 'year'
    };

    const eventTimeFormat = {
        hour: 'numeric',
        minute: '2-digit',
        omitZeroMinute: true,
        meridiem: true
    };

    const handleEventClick = info => {
        if (info.event.url) {
            window.open(info.event.url);
            info.jsEvent.preventDefault();
        } else {
            setModalEventContent(info);
            setIsOpenModal(true);
        }
    }

    return (
        <Fragment>
            <PageHeader
                title="Reservaciones"
                titleTag="h2"
                description={'Gestiona las reservaciones del condominio.'}
                className="mb-3"
                col={{ lg: 9 }}
            >
                <SupportLink />
            </PageHeader>
            <Card className="mb-3">
                <CardHeader className='bg-light'>
                    <Row>
                        <Col tag={Flex} align='center' justify='between'>
                            <h5>Reservaciones del mes de {moment(new Date()).format('MMMM, YYYY')}
                            </h5>
                            <Flex align='center'>
                                <ButtonIcon color='falcon-default' className='mr-2' size='md' icon={faUndo} onClick={() => history.push('/dashboard/areas')} >Volver a áreas comunes</ButtonIcon>
                                <If cond={['property']}>
                                    <Or value={role}>
                                        <ButtonIcon color='primary' className='mr-2' size='md' onClick={() => setIsOpenScheduleModal(true)} icon={faPlus}>Añadir reserva</ButtonIcon>
                                    </Or>
                                </If>
                            </Flex>
                        </Col>
                    </Row>
                </CardHeader>
                <CardBody>
                    <PreloadState loading={data.isLoading}>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, interactionPlugin, bootstrapPlugin]}
                            initialView="dayGridMonth"
                            themeSystem="bootstrap"
                            height={800}
                            dayMaxEvents={1}
                            headerToolbar={false}
                            select={(info) => {
                                if (role === 'property') {
                                    setIsOpenScheduleModal(true);
                                    setAddScheduleStartDate(info.start);
                                }
                            }}
                            events={events}
                            locale={'es'}
                            editable
                            selectable
                            selectConstraint={{
                                start: moment().format('YYYY-MM-DD'),
                                end: moment(endOfMonth).add(1, 'day'),
                            }}
                            defaultAllDay={true}
                            buttonText={buttonText}
                            eventClick={handleEventClick}
                            eventTimeFormat={eventTimeFormat}
                            showNonCurrentDates={false}
                        />

                        <AddScheduleModal
                            isOpenScheduleModal={isOpenScheduleModal}
                            setIsOpenScheduleModal={setIsOpenScheduleModal}
                            initialEvents={initialEvents}
                            setInitialEvents={setInitialEvents}
                            addScheduleStartDate={addScheduleStartDate}
                            setAddScheduleStartDate={setAddScheduleStartDate}
                            validations={{ rental: !!state?.rental_fee, deposit: !!state?.deposit_fee }}
                            id={id}
                            mutate={() => mutate()}
                        />

                        <CalendarEventModal
                            isOpenModal={isOpenModal}
                            setIsOpenModal={setIsOpenModal}
                            modalEventContent={modalEventContent}
                            addScheduleStartDate={addScheduleStartDate}
                            mutate={() => mutate()}
                        />
                    </PreloadState>
                </CardBody>
            </Card>
        </Fragment>
    )
}

export default Reservations;